import React from 'react';
import { Utensils, Sparkles, Clock, Target, CheckCircle, ArrowRight, Building2, Users, Shield, Zap, Settings, Lightbulb } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveRoboticsPage: React.FC = () => {
  useSEO('cognitive-robotics');
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
      <section
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl mx-auto mb-8 flex items-center justify-center">
            <Utensils className="w-10 h-10 text-white" />
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
            style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
          >
            Cognitive Robotics
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Your trusted automation partner for implementing intelligent robotics solutions. We handle vendor selection,
            system integration, deployment, and ongoing support - delivering complete end-to-end robotics automation for
            hotels, restaurants, healthcare facilities, and commercial operations.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Robotics Implementation Services</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Needs Assessment & Strategy</h3>
                    <p className="text-gray-700 dark:text-gray-300">Comprehensive analysis of your operations to identify automation opportunities and develop a customized robotics roadmap.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Vendor Selection & Procurement</h3>
                    <p className="text-gray-700 dark:text-gray-300">Expert guidance in selecting the right robotics vendors and solutions that match your specific requirements and budget.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">System Integration & Deployment</h3>
                    <p className="text-gray-700 dark:text-gray-300">Seamless integration with your existing systems including PMS, POS, and facility management platforms.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Training & Change Management</h3>
                    <p className="text-gray-700 dark:text-gray-300">Comprehensive staff training and change management support to ensure successful adoption and maximum ROI.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Robotics Solutions We Implement</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Cleaning & Sanitation Robots</h3>
                    <p className="text-gray-700 dark:text-gray-300">Autonomous floor cleaning, UV disinfection, and sanitation systems for hotels, hospitals, and commercial facilities.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Food & Beverage Service Robots</h3>
                    <p className="text-gray-700 dark:text-gray-300">Delivery robots for restaurants, room service, and event catering with intelligent navigation and safety features.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Concierge & Guest Service Robots</h3>
                    <p className="text-gray-700 dark:text-gray-300">Interactive robots for information, check-in assistance, and multilingual guest communication.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Custom Fleet Management</h3>
                    <p className="text-gray-700 dark:text-gray-300">Centralized monitoring and management platforms for multi-robot operations across single or multiple locations.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Our Implementation Benefits */}
          <section className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Full-Service Implementation Benefits</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Lightbulb className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Expert Consultation</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">Strategic guidance from robotics automation specialists</p>
              </div>
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Settings className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Seamless Integration</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">Complete integration with your existing systems</p>
              </div>
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Proven ROI</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">30-50% operational cost reduction</p>
              </div>
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Ongoing Support</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">24/7 monitoring and maintenance support</p>
              </div>
            </div>
          </section>

          {/* Target Industries */}
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Industries We Serve</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Hotels & Resorts</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Implementing room service delivery, housekeeping automation, lobby services, and cleaning solutions that elevate guest experiences.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Utensils className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Restaurants & Cafes</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Deploying food delivery, bussing assistance, and cleaning robots that optimize operations and reduce labor costs.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Healthcare Facilities</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Integrating UV disinfection, supply delivery, and medication transport robots with HIPAA-compliant systems.
                </p>
              </div>
            </div>
          </section>

          {/* Why Choose CCH */}
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Why Choose Cognitive Craft Hub</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start">
                <Sparkles className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-4 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Vendor-Agnostic Approach</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    We evaluate and recommend the best robotics solutions from leading manufacturers based on your specific needs, ensuring you get optimal value.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Sparkles className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-4 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">End-to-End Project Management</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    From initial assessment through deployment and beyond, we manage every aspect of your robotics implementation with dedicated support.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Sparkles className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-4 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">AI-Powered Integration</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Leverage our Cognitive AI platform to enhance robotics capabilities with advanced vision, voice interaction, and predictive analytics.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Sparkles className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-4 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Comprehensive Training & Support</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Ensure staff adoption with thorough training programs, documentation, and 24/7 technical support for ongoing operations.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Important Disclaimers */}
          <section className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8 mb-16">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mr-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                Important Legal Disclaimers
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">Third-Party Products</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  Cognitive Craft Hub acts as a consultant and systems integrator. We do not manufacture robotics hardware. All equipment is subject to manufacturer warranties. We are not liable for third-party product defects or malfunctions.
                </p>
              </div>
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">Safety & Operations</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  Clients are responsible for safe robotics operation, OSHA compliance, staff training, and maintenance. We provide implementation services but are not liable for operational incidents or injuries.
                </p>
              </div>
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">Healthcare Compliance</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  Healthcare clients are solely responsible for HIPAA, FDA, and healthcare regulatory compliance. Final compliance responsibility rests with your organization.
                </p>
              </div>
              <div className="bg-white/80 dark:bg-white/10 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">Performance Estimates</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  Cost reduction figures (30-50%) are estimates based on industry averages and are NOT guaranteed. Actual results vary based on implementation, industry, and operational factors.
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-blue-200 dark:border-blue-500/30">
              <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                <strong>Full legal terms:</strong> By engaging our services, you agree to our complete{' '}
                <a href="/terms" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">
                  Terms & Conditions
                </a>
                {' '}including sections on Third-Party Products (Section 9), Robotics Safety (Section 10), Healthcare Compliance (Section 11), and Performance Claims (Section 12).
              </p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Implement Robotics Automation?
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8 max-w-3xl mx-auto">
              Partner with Cognitive Craft Hub to transform your operations with intelligent robotics.
              Let our experts guide you through every step of your automation journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Request Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Consultation
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
    </>
  );
};
